import React, { useEffect, useState } from "react";
import { Download, Smartphone } from "lucide-react";

/**
 * A subtle "Install the app" button. Uses the same beforeinstallprompt event
 * captured by InstallPWA, or falls back to iOS instructions. Hidden entirely
 * when the app is already installed.
 */
function isIos() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent || "") && !window.MSStream;
}
function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export function InstallAppButton({ label = "Install the app" }) {
  const [deferred, setDeferred] = useState(null);
  const [supported, setSupported] = useState(false);
  const [showIos, setShowIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    setSupported(isIos()); // iOS always has a manual path
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setSupported(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  if (!supported && !deferred && !isIos()) return null;
  if (isStandalone()) return null;

  const click = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice.catch(() => null);
      setDeferred(null);
    } else if (isIos()) {
      setShowIos(true);
    }
  };

  return (
    <>
      <button
        data-testid="install-app-btn"
        onClick={click}
        className="btn-pill bg-white/70 border border-ink/10 text-ink hover:bg-white flex items-center gap-2 text-sm"
      >
        <Smartphone size={14} /> {label}
      </button>
      {showIos && (
        <div
          data-testid="install-ios-modal"
          className="fixed inset-0 z-[90] flex items-end md:items-center justify-center bg-ink/60 backdrop-blur-sm"
          onClick={() => setShowIos(false)}
        >
          <div
            className="card-paper w-full md:max-w-md m-4 p-6 space-y-3"
            style={{ border: "2px double #D4A574" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="eyebrow text-ochre-300">Install on iPhone</div>
            <h3 className="font-display text-2xl text-ink" style={{ fontFamily: "Fraunces" }}>
              Add to Home Screen.
            </h3>
            <ol className="space-y-2 text-sm text-ink/75 mt-2">
              <li>1. Tap Safari's <strong>Share</strong> button (the square with an up-arrow).</li>
              <li>2. Scroll and tap <strong>Add to Home Screen</strong>.</li>
              <li>3. Tap <strong>Add</strong> in the top-right.</li>
            </ol>
            <button
              onClick={() => setShowIos(false)}
              className="btn-pill bg-ink text-parchment hover:bg-ivy w-full mt-2"
            >
              <Download size={14} className="inline mr-1" /> Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
